/* BP-018 · Buyers tab — Session 1.
 *
 * Two-pane property-scoped workspace tab:
 *   Left  : Matched Buyers list (persisted output of last match run).
 *   Right : Actions panel (Run Match, Manage Buyers modal).
 *
 * Follows the same conventions as LeadScoringTab.jsx.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, Sparkles, Trophy, MapPin, DollarSign,
          Home, Ruler, Clock, Plus, Trash2, Pencil, X, Check,
          AlertTriangle, HandCoins, FileSignature, ArrowRight,
          Ban, Handshake, Megaphone, Eye, Send,
          Brain, TrendingUp, Activity, RotateCcw } from "lucide-react";


const OFFER_STATUS_COPY = {
  submitted: { label: "Submitted", tone: "blue" },
  countered: { label: "Countered", tone: "amber" },
  accepted:  { label: "Accepted",  tone: "emerald" },
  assigned:  { label: "Assigned",  tone: "violet" },
  closed:    { label: "Closed",    tone: "slate" },
  rejected:  { label: "Rejected",  tone: "rose" },
  withdrawn: { label: "Withdrawn", tone: "stone" },
  expired:   { label: "Expired",   tone: "stone" },
};


function OfferStatusBadge({ status }) {
  const info = OFFER_STATUS_COPY[status] || { label: status, tone: "slate" };
  const cls = {
    blue:    "bg-blue-100 text-blue-800 border-blue-200",
    amber:   "bg-amber-100 text-amber-800 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    violet:  "bg-violet-100 text-violet-800 border-violet-200",
    slate:   "bg-slate-100 text-slate-800 border-slate-200",
    rose:    "bg-rose-100 text-rose-800 border-rose-200",
    stone:   "bg-stone-100 text-stone-700 border-stone-200",
  }[info.tone];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
           data-testid={`offer-status-${status}`}>
      {info.label}
    </span>
  );
}


function fmtMoney(v) {
  if (v == null) return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!isFinite(n)) return "—";
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}


function OfferSubmitModal({ buyer, propertyId, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    offer_amount: "", earnest_money: "", closing_days: 21,
    terms: "cash",
    contingencies: ["inspection"],
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/v3/disposition/offer", {
        buyer_id: buyer.buyer_id, property_id: propertyId,
        offer_amount: form.offer_amount,
        earnest_money: form.earnest_money || null,
        closing_days: Number(form.closing_days) || 30,
        contingencies: form.contingencies, terms: form.terms || null,
      });
      toast.success(`Offer sent to ${buyer.buyer_name}.`);
      onSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Offer failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          data-testid="offer-submit-modal">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">Submit Offer</div>
            <div className="text-xs text-slate-500">to {buyer.buyer_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Offer amount ($) *</label>
            <input type="number" required min="0" value={form.offer_amount}
                    onChange={(e) => setForm({...form, offer_amount: e.target.value})}
                    className="input w-full text-sm mt-1"
                    data-testid="offer-amount-input"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Earnest ($)</label>
              <input type="number" min="0" value={form.earnest_money}
                      onChange={(e) => setForm({...form, earnest_money: e.target.value})}
                      className="input w-full text-sm mt-1"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Closing days</label>
              <input type="number" min="1" max="365" value={form.closing_days}
                      onChange={(e) => setForm({...form, closing_days: e.target.value})}
                      className="input w-full text-sm mt-1"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Contingencies</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {["inspection", "financing", "appraisal", "title"].map((c) => {
                const on = form.contingencies.includes(c);
                return (
                  <button type="button" key={c}
                          onClick={() => setForm({...form,
                            contingencies: on
                              ? form.contingencies.filter((x) => x !== c)
                              : [...form.contingencies, c]})}
                          className={`text-xs font-semibold px-2 py-1 rounded border ${
                            on ? "bg-indigo-600 text-white border-indigo-600"
                                : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Terms</label>
            <input type="text" value={form.terms}
                    onChange={(e) => setForm({...form, terms: e.target.value})}
                    placeholder="cash / conventional / private / …"
                    className="input w-full text-sm mt-1"/>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose}
                    className="px-3 py-1.5 text-sm font-semibold rounded border border-slate-200 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-1.5 rounded inline-flex items-center gap-1"
                    data-testid="offer-submit-confirm">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <HandCoins size={12}/>}
              Send Offer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function OfferCard({ offer, onAction }) {
  const [busy, setBusy] = useState(null);
  const doAction = async (action, body = {}) => {
    setBusy(action);
    try {
      const { data } = await api.post(
        `/v3/disposition/offers/${offer.id}/${action}`, body);
      toast.success(`Offer ${data.status}.`);
      onAction(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(null); }
  };
  const isPending = ["submitted", "countered"].includes(offer.status);
  const isAccepted = offer.status === "accepted";
  const isAssigned = offer.status === "assigned";

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2"
          data-testid={`offer-card-${offer.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <OfferStatusBadge status={offer.status}/>
          <span className="text-sm font-semibold tabular-nums" data-testid={`offer-amount-${offer.id}`}>
            {fmtMoney(offer.offer_amount)}
          </span>
          {offer.assignment_fee && (
            <span className="text-[10px] text-emerald-700 font-bold">
              +{fmtMoney(offer.assignment_fee)} fee
            </span>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {isPending && (
            <>
              <button onClick={() => {
                const amt = window.prompt("Counter amount ($):", offer.offer_amount);
                if (amt) doAction("counter", { counter_amount: amt });
              }} disabled={!!busy}
                       className="text-xs font-semibold text-blue-700 hover:bg-blue-50 px-2 py-1 rounded border border-blue-200"
                       data-testid={`offer-counter-${offer.id}`}>
                Counter
              </button>
              <button onClick={() => {
                const fee = window.prompt("Assignment fee ($):", "10000");
                if (fee) doAction("accept", { assignment_fee: fee });
              }} disabled={!!busy}
                       className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded"
                       data-testid={`offer-accept-${offer.id}`}>
                {busy === "accept" ? <Loader2 size={11} className="animate-spin"/> : "Accept"}
              </button>
              <button onClick={() => doAction("reject", {
                reason: window.prompt("Reason (optional):") || ""
              })} disabled={!!busy}
                       className="text-xs font-semibold text-rose-700 hover:bg-rose-50 px-2 py-1 rounded border border-rose-200"
                       data-testid={`offer-reject-${offer.id}`}>
                <Ban size={11}/>
              </button>
            </>
          )}
          {isAccepted && (
            <button onClick={() => doAction("assign", {
              contract_url: window.prompt("Contract URL (optional):") || null,
            })} disabled={!!busy}
                     className="text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 px-2 py-1 rounded inline-flex items-center gap-1"
                     data-testid={`offer-assign-${offer.id}`}>
              <FileSignature size={11}/> Assign
            </button>
          )}
          {isAssigned && (
            <button onClick={() => doAction("close", {
              final_closing_amount: window.prompt("Final closing amount ($):",
                offer.offer_amount) || null
            })} disabled={!!busy}
                     className="text-xs font-semibold text-white bg-slate-800 hover:bg-slate-900 px-2 py-1 rounded inline-flex items-center gap-1"
                     data-testid={`offer-close-${offer.id}`}>
              <Handshake size={11}/> Close
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
        <span>Buyer: {offer.buyer_id?.slice(0, 8)}…</span>
        <ArrowRight size={9}/>
        <span>{offer.closing_days}d close</span>
        {offer.contingencies?.length > 0 && (
          <span className="text-[10px] italic">· {offer.contingencies.join(", ")}</span>
        )}
      </div>
    </div>
  );
}


const TIMING_LABELS = {
  now: "Ready now", "30d": "Next 30 days", "90d": "Next 90 days",
  "6mo": "Next 6 months", "12mo": "Next year", watching: "Just watching",
};
const SUBSCORE_ICONS = {
  geo: MapPin, price: DollarSign, type: Home, size: Ruler,
  timing: Clock, strategy: Trophy,
};


function CompositeBadge({ score }) {
  const cls = score >= 80 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
             : score >= 60 ? "bg-blue-100 text-blue-800 border-blue-200"
             : score >= 40 ? "bg-amber-100 text-amber-800 border-amber-200"
                            : "bg-rose-100 text-rose-800 border-rose-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
           data-testid="buyer-composite-badge">
      <Sparkles size={9}/> {Number(score).toFixed(0)}
    </span>
  );
}


function SubScorePills({ subScores }) {
  return (
    <div className="grid grid-cols-3 gap-1.5 mt-2">
      {Object.entries(subScores).map(([k, s]) => {
        const Icon = SUBSCORE_ICONS[k] || Sparkles;
        return (
          <div key={k}
                className="rounded border border-slate-200 bg-white px-1.5 py-1"
                title={s.note}
                data-testid={`buyer-subscore-${k}`}>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold inline-flex items-center gap-1">
              <Icon size={9}/> {k}
            </div>
            <div className="tabular-nums font-semibold text-slate-800 text-xs">
              {(s.value * 100).toFixed(0)}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function BuyerRolodexModal({ open, onClose, onChanged }) {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);        // buyer being edited (or "new")
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/disposition/buyers");
      setBuyers(data.items || []);
    } catch {
      toast.error("Failed to load buyers.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const del = async (id) => {
    if (!window.confirm("Archive this buyer?")) return;
    setSavingId(id);
    try {
      await api.delete(`/v3/disposition/buyers/${id}`);
      toast.success("Buyer archived.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed.");
    } finally { setSavingId(null); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="buyer-rolodex-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">
              Buyer Rolodex
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {buyers.length} active {buyers.length === 1 ? "buyer" : "buyers"}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={() => setEditing({ __new: true })}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"
                    data-testid="buyer-rolodex-add">
              <Plus size={12}/> Add Buyer
            </button>
            <button onClick={onClose}
                    className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
                    data-testid="buyer-rolodex-close">×</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? <Loader2 size={20} className="animate-spin text-slate-400 mx-auto mt-4"/>
                    : buyers.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-8">
              No buyers yet. Add one to start matching.
            </div>
          ) : buyers.map((b) => (
            <div key={b.id}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                  data-testid={`buyer-row-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-slate-900">{b.name}</div>
                  <div className="text-xs text-slate-500">
                    {b.email || "no email"} · {b.company || "no company"}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing(b)}
                          className="text-xs text-slate-500 hover:text-slate-900 p-1"
                          data-testid={`buyer-edit-${b.id}`}>
                    <Pencil size={12}/>
                  </button>
                  <button onClick={() => del(b.id)}
                          disabled={savingId === b.id}
                          className="text-xs text-rose-500 hover:text-rose-700 p-1"
                          data-testid={`buyer-delete-${b.id}`}>
                    {savingId === b.id ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {editing && (
        <BuyerEditorModal buyer={editing.__new ? null : editing}
                            onClose={() => setEditing(null)}
                            onSaved={async () => {
                              setEditing(null);
                              await load();
                              onChanged?.();
                            }}/>
      )}
    </div>
  );
}


function BuyerEditorModal({ buyer, onClose, onSaved }) {
  const isNew = !buyer;
  const [form, setForm] = useState(() => ({
    name: buyer?.name || "",
    email: buyer?.email || "",
    phone: buyer?.phone || "",
    company: buyer?.company || "",
    notes: buyer?.notes || "",
    tags:   (buyer?.tags || []).join(", "),
  }));
  const [prefs, setPrefs] = useState({
    property_types: [], zip_codes_csv: "", cities_csv: "", states_csv: "",
    min_price: "", max_price: "", min_bedrooms: "", min_bathrooms: "",
    min_sqft: "", max_sqft: "", timing: "watching",
    exclude_hoa: false, exclude_flood: false, exclude_rehab: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (!buyer?.id) return;
      setLoading(true);
      try {
        const { data } = await api.get(
          `/v3/disposition/buyers/${buyer.id}/preferences`);
        if (!data?.empty) {
          setPrefs({
            property_types: data.property_types || [],
            zip_codes_csv: (data.zip_codes || []).join(", "),
            cities_csv:    (data.cities || []).join(", "),
            states_csv:    (data.states || []).join(", "),
            min_price: data.min_price ?? "", max_price: data.max_price ?? "",
            min_bedrooms: data.min_bedrooms ?? "",
            min_bathrooms: data.min_bathrooms ?? "",
            min_sqft: data.min_sqft ?? "", max_sqft: data.max_sqft ?? "",
            timing: data.timing || "watching",
            exclude_hoa: !!data.exclude_hoa,
            exclude_flood: !!data.exclude_flood,
            exclude_rehab: !!data.exclude_rehab,
          });
        }
      } catch { /* preferences endpoint returned no doc */ }
      finally { setLoading(false); }
    })();
  }, [buyer?.id]);

  const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let id = buyer?.id;
      const buyerPayload = {
        name: form.name, email: form.email || null, phone: form.phone || null,
        company: form.company || null, notes: form.notes || null,
        tags: csv(form.tags),
      };
      if (isNew) {
        const { data } = await api.post("/v3/disposition/buyers", buyerPayload);
        id = data.id;
      } else {
        await api.patch(`/v3/disposition/buyers/${id}`, buyerPayload);
      }
      // Only upsert preferences if the user set anything.
      const prefPayload = {
        property_types: prefs.property_types,
        zip_codes: csv(prefs.zip_codes_csv),
        cities:    csv(prefs.cities_csv),
        states:    csv(prefs.states_csv),
        min_price: prefs.min_price !== "" ? Number(prefs.min_price) : null,
        max_price: prefs.max_price !== "" ? Number(prefs.max_price) : null,
        min_bedrooms:  prefs.min_bedrooms  !== "" ? Number(prefs.min_bedrooms)  : null,
        min_bathrooms: prefs.min_bathrooms !== "" ? Number(prefs.min_bathrooms) : null,
        min_sqft: prefs.min_sqft !== "" ? Number(prefs.min_sqft) : null,
        max_sqft: prefs.max_sqft !== "" ? Number(prefs.max_sqft) : null,
        timing: prefs.timing,
        exclude_hoa: prefs.exclude_hoa,
        exclude_flood: prefs.exclude_flood,
        exclude_rehab: prefs.exclude_rehab,
        strategies: [],
      };
      await api.put(`/v3/disposition/buyers/${id}/preferences`, prefPayload);
      toast.success(isNew ? "Buyer added." : "Buyer updated.");
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          data-testid="buyer-editor-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10 flex items-center justify-between">
          <div className="font-display font-extrabold text-lg">
            {isNew ? "Add Buyer" : `Edit — ${buyer.name}`}
          </div>
          <button onClick={onClose}
                  className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-4">
          {loading && <div className="text-xs text-slate-500">Loading preferences…</div>}

          {/* Contact */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Name *" required
                     value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                     className="input w-full text-sm" data-testid="buyer-editor-name"/>
              <input placeholder="Company"
                     value={form.company} onChange={(e) => setForm({...form, company: e.target.value})}
                     className="input w-full text-sm" data-testid="buyer-editor-company"/>
              <input type="email" placeholder="Email"
                     value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                     className="input w-full text-sm" data-testid="buyer-editor-email"/>
              <input placeholder="Phone"
                     value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                     className="input w-full text-sm" data-testid="buyer-editor-phone"/>
              <input placeholder="Tags (comma-separated)" className="col-span-2 input w-full text-sm"
                     value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}
                     data-testid="buyer-editor-tags"/>
              <textarea placeholder="Notes" rows={2} className="col-span-2 input w-full text-sm"
                        value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            </div>
          </div>

          {/* Preferences */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Preferences (match criteria)</div>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Zips (78745, 78704)" className="input w-full text-sm"
                     value={prefs.zip_codes_csv} onChange={(e) => setPrefs({...prefs, zip_codes_csv: e.target.value})}
                     data-testid="buyer-prefs-zips"/>
              <input placeholder="States (TX, FL)" className="input w-full text-sm"
                     value={prefs.states_csv} onChange={(e) => setPrefs({...prefs, states_csv: e.target.value})}
                     data-testid="buyer-prefs-states"/>
              <input placeholder="Cities" className="col-span-2 input w-full text-sm"
                     value={prefs.cities_csv} onChange={(e) => setPrefs({...prefs, cities_csv: e.target.value})}
                     data-testid="buyer-prefs-cities"/>
              <input type="number" placeholder="Min price $" className="input w-full text-sm"
                     value={prefs.min_price} onChange={(e) => setPrefs({...prefs, min_price: e.target.value})}
                     data-testid="buyer-prefs-min-price"/>
              <input type="number" placeholder="Max price $" className="input w-full text-sm"
                     value={prefs.max_price} onChange={(e) => setPrefs({...prefs, max_price: e.target.value})}
                     data-testid="buyer-prefs-max-price"/>
              <input type="number" placeholder="Min bedrooms" className="input w-full text-sm"
                     value={prefs.min_bedrooms} onChange={(e) => setPrefs({...prefs, min_bedrooms: e.target.value})}
                     data-testid="buyer-prefs-min-beds"/>
              <input type="number" step="0.5" placeholder="Min bathrooms" className="input w-full text-sm"
                     value={prefs.min_bathrooms} onChange={(e) => setPrefs({...prefs, min_bathrooms: e.target.value})}
                     data-testid="buyer-prefs-min-baths"/>
              <input type="number" placeholder="Min sqft" className="input w-full text-sm"
                     value={prefs.min_sqft} onChange={(e) => setPrefs({...prefs, min_sqft: e.target.value})}/>
              <input type="number" placeholder="Max sqft" className="input w-full text-sm"
                     value={prefs.max_sqft} onChange={(e) => setPrefs({...prefs, max_sqft: e.target.value})}/>

              <select value={prefs.timing}
                       onChange={(e) => setPrefs({...prefs, timing: e.target.value})}
                       className="col-span-2 input w-full text-sm"
                       data-testid="buyer-prefs-timing">
                {Object.entries(TIMING_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{`${v} (${k})`}</option>
                ))}
              </select>

              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Property Types</div>
                <div className="flex flex-wrap gap-1.5">
                  {["single_family","multi_family","condo","townhouse","land","mobile","commercial","mixed_use"].map((t) => {
                    const on = prefs.property_types.includes(t);
                    return (
                      <button type="button" key={t}
                              onClick={() => setPrefs({...prefs,
                                property_types: on
                                  ? prefs.property_types.filter((x) => x !== t)
                                  : [...prefs.property_types, t]})}
                              className={`text-xs font-semibold px-2 py-1 rounded border ${
                                on ? "bg-indigo-600 text-white border-indigo-600"
                                    : "border-slate-200 text-slate-700 hover:border-slate-400"}`}
                              data-testid={`buyer-prefs-type-${t}`}>
                        {t.replace("_", " ")}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-2 flex flex-wrap gap-3 text-xs">
                {[["exclude_hoa","No HOA"], ["exclude_flood","No flood zone"], ["exclude_rehab","No rehab needed"]].map(([k,label]) => (
                  <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!prefs[k]}
                            onChange={(e) => setPrefs({...prefs, [k]: e.target.checked})}
                            data-testid={`buyer-prefs-${k}`}/>
                    <span className="text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose}
                    className="px-3 py-1.5 text-sm font-semibold rounded border border-slate-200 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-1.5 rounded inline-flex items-center gap-1"
                    data-testid="buyer-editor-save">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function CampaignModal({ property, onClose }) {
  const [name, setName] = useState(
    `${property.address?.street || "Property"} · new deal`);
  const [template, setTemplate] = useState(
    "Hey {name}, new deal at {address}, {city} — ${price}. Reply YES for details. Text STOP to opt out.");
  const [statesCsv, setStatesCsv] = useState(
    (property.address?.state || "").toUpperCase());
  const [timings, setTimings] = useState(["now", "30d", "90d"]);
  const [campaign, setCampaign] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const createDraft = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/v3/disposition/campaigns", {
        name, channel: "sms",
        template_body: template,
        segment: {
          states: statesCsv.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
          timings,
        },
        property_id: property.id,
      });
      setCampaign(data);
      toast.success("Draft created.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setBusy(null); }
  };

  const doPreview = async () => {
    if (!campaign) return;
    setBusy("preview");
    try {
      const { data } = await api.post(
        `/v3/disposition/campaigns/${campaign.id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(null); }
  };

  const doSend = async () => {
    if (!campaign) return;
    if (!window.confirm(
      `Send SMS to ${preview?.target_count || "?"} buyers now? ` +
      `Real Twilio messages will be dispatched.`)) return;
    setBusy("send");
    try {
      const { data } = await api.post(
        `/v3/disposition/campaigns/${campaign.id}/send`,
        { dry_run: false });
      setCampaign(data);
      toast.success(`Campaign ${data.status}: ${data.sent_count} sent, ${data.failed_count} failed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          data-testid="campaign-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10 flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg leading-tight inline-flex items-center gap-1.5">
              <Megaphone size={16} className="text-indigo-600"/> Bulk Buyer Campaign
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              SMS blast to matching buyers · Twilio-billed at your workspace rate
            </div>
          </div>
          <button onClick={onClose}
                  className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>

        <div className="p-4 space-y-4">
          {!campaign && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Campaign name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                        className="input w-full text-sm mt-1"
                        data-testid="campaign-name"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Message template
                </label>
                <textarea rows={4} value={template}
                            onChange={(e) => setTemplate(e.target.value)}
                            className="input w-full text-sm mt-1 font-mono"
                            data-testid="campaign-template"/>
                <div className="text-[10px] text-slate-500 mt-1">
                  Placeholders: {"{name} {company} {address} {city} {state} {price}"}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Segment · States</label>
                <input value={statesCsv} onChange={(e) => setStatesCsv(e.target.value)}
                        placeholder="TX, FL"
                        className="input w-full text-sm mt-1"
                        data-testid="campaign-segment-states"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Segment · Timings</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {Object.entries(TIMING_LABELS).map(([k, v]) => {
                    const on = timings.includes(k);
                    return (
                      <button type="button" key={k}
                              onClick={() => setTimings(on
                                ? timings.filter((x) => x !== k)
                                : [...timings, k])}
                              className={`text-xs font-semibold px-2 py-1 rounded border ${
                                on ? "bg-indigo-600 text-white border-indigo-600"
                                    : "border-slate-200 text-slate-700 hover:border-slate-400"}`}
                              data-testid={`campaign-timing-${k}`}>
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button onClick={createDraft} disabled={busy === "create"}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded inline-flex items-center justify-center gap-1.5"
                      data-testid="campaign-create-draft">
                {busy === "create" ? <Loader2 size={12} className="animate-spin"/>
                                    : <Plus size={12}/>}
                Create Draft
              </button>
            </>
          )}

          {campaign && (
            <>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Draft campaign</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5"
                          data-testid="campaign-name-echo">
                      {campaign.name}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${
                    campaign.status === "sent"
                      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                      : "bg-slate-200 text-slate-700 border-slate-300"}`}
                        data-testid="campaign-status">
                    {campaign.status}
                  </span>
                </div>
                {campaign.status === "sent" && (
                  <div className="mt-2 text-xs text-slate-700">
                    Sent: <b>{campaign.sent_count}</b> · Failed: <b>{campaign.failed_count}</b> · Target: <b>{campaign.target_count}</b>
                  </div>
                )}
              </div>

              {preview && (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3"
                      data-testid="campaign-preview">
                  <div className="text-xs uppercase tracking-wider text-blue-800 font-bold mb-1">
                    Preview — {preview.target_count} recipient(s)
                  </div>
                  {preview.sample ? (
                    <div className="text-xs text-slate-700 font-mono whitespace-pre-wrap"
                          data-testid="campaign-preview-rendered">
                      To {preview.sample.buyer_name}: {preview.sample.rendered}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 italic">
                      No matching buyers with phone numbers.
                    </div>
                  )}
                </div>
              )}

              {campaign.status === "draft" && (
                <div className="flex gap-2">
                  <button onClick={doPreview} disabled={busy === "preview"}
                          className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2 rounded inline-flex items-center justify-center gap-1.5"
                          data-testid="campaign-preview-btn">
                    {busy === "preview" ? <Loader2 size={12} className="animate-spin"/> : <Eye size={12}/>}
                    Preview Recipients
                  </button>
                  <button onClick={doSend} disabled={busy === "send" || !preview || !preview.target_count}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                          data-testid="campaign-send-btn">
                    {busy === "send" ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
                    Send Now
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export default function BuyersTab({ property }) {
  const [matches, setMatches] = useState([]);
  const [offers, setOffers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rolodexOpen, setRolodexOpen] = useState(false);
  const [offerBuyer, setOfferBuyer] = useState(null);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [coachMode, setCoachMode] = useState("pricing_guidance");
  const [coach, setCoach] = useState(null);
  const [coachBusy, setCoachBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, oRes, dRes] = await Promise.allSettled([
        api.get(`/v3/disposition/properties/${property.id}/matches`),
        api.get(`/v3/disposition/properties/${property.id}/offers`),
        api.get("/v3/disposition/dashboard?window_days=30"),
      ]);
      if (mRes.status === "fulfilled") setMatches(mRes.value.data.items || []);
      if (oRes.status === "fulfilled") setOffers(oRes.value.data.items || []);
      if (dRes.status === "fulfilled") { setDashboard(dRes.value.data); }
      else if (dRes.status === "rejected") {
        // Silent-fail is a footgun — surface it as a console warn so the
        // KPI banner never disappears without a trace.
        console.warn("[BuyersTab] dashboard load failed:", dRes.reason?.message);
        setDashboard(null);
      }
    } catch { /* likely empty */ }
    finally { setLoading(false); }
  }, [property.id]);

  const loadCoach = useCallback(async (mode) => {
    try {
      const { data } = await api.get(
        `/v3/disposition/properties/${property.id}/coach?mode=${mode}`);
      setCoach(data);
    } catch { setCoach(null); }
  }, [property.id]);

  useEffect(() => { loadCoach(coachMode); }, [loadCoach, coachMode]);

  const generateCoach = useCallback(async (force = false) => {
    setCoachBusy(true);
    try {
      const { data } = await api.post(
        `/v3/disposition/properties/${property.id}/coach`,
        { mode: coachMode, force });
      setCoach(data);
      toast.success(data.cached ? "Coaching from cache." : "AI coaching generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI Coach failed.");
    } finally { setCoachBusy(false); }
  }, [property.id, coachMode]);

  useEffect(() => { load(); }, [load]);

  // ReSimpli-style auto-match: when a deal enters a disposition-heavy
  // stage (Under Contract / Marketing / Pending Close) and no match has
  // been computed yet, silently fire the match engine so operators see
  // their top ranked cash buyers the instant they land on the tab — no
  // "Run Match" button click required. Fires at most once per mount.
  const autoMatchFired = useRef(false);
  const HOT_STAGES = useMemo(() => new Set(["under_contract", "marketing", "pending_close"]), []);

  const runMatch = useCallback(async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/v3/disposition/match", {
        property_id: property.id, min_score: 40.0, limit: 25,
      });
      setMeta({
        considered: data.considered, kept: data.kept,
        computed_at: data.computed_at, weights: data.weights,
      });
      setMatches(data.results || []);
      if (data.message) {
        toast.info(data.message);
      } else if ((data.kept || 0) === 0) {
        toast.info(`Scored ${data.considered} buyers — none crossed the min-score gate.`);
      } else {
        toast.success(`Matched ${data.kept} of ${data.considered} buyers.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Match failed.");
    } finally { setRunning(false); }
  }, [property.id]);

  // Auto-fire the match on first mount for hot-stage deals with no
  // existing matches. Waits for `loading` to settle so we don't race
  // the initial /matches read that could turn up cached results.
  useEffect(() => {
    if (loading || autoMatchFired.current) return;
    if (!HOT_STAGES.has(property.stage)) return;
    if (matches.length > 0) return;
    autoMatchFired.current = true;
    runMatch();
  }, [loading, matches.length, property.stage, HOT_STAGES, runMatch]);

  const summary = useMemo(() => {
    if (!matches.length) return null;
    const top = matches[0];
    return {
      top_score: top.composite_score,
      top_name:  top.buyer_name,
      count:     matches.length,
    };
  }, [matches]);

  return (
    <div className="space-y-4" data-testid="buyers-tab">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} className="text-indigo-600"/>
              <div className="font-display font-extrabold text-lg leading-tight">
                Match &amp; Blast
              </div>
            </div>
            <div className="text-xs text-slate-500 max-w-md">
              Deterministic match across geography, price, deal type, size, and
              timing — then blast the top buyers via SMS in one click.
              Weights: geo 30 · price 25 · type 20 · size 15 · timing 10.
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setCampaignOpen(true)}
                    className="bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white text-xs font-black uppercase tracking-[0.06em] px-3 py-1.5 rounded shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                    disabled={!matches.length}
                    data-testid="buyers-open-campaign">
              <Megaphone size={12}/>
              {matches.length ? `Blast ${matches.length} Buyer${matches.length === 1 ? "" : "s"}` : "Blast Buyers"}
            </button>
            <button onClick={() => setRolodexOpen(true)}
                    className="border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1.5"
                    data-testid="buyers-open-rolodex">
              <Users size={12}/> Manage Buyers
            </button>
            <button onClick={runMatch} disabled={running}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="buyers-run-match">
              {running ? <Loader2 size={12} className="animate-spin"/>
                        : <Sparkles size={12}/>}
              {matches.length ? "Re-run Match" : "Run Match"}
            </button>
          </div>
        </div>
        {summary && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Top match</div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="buyers-top-name">
                {summary.top_name}
              </div>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Top score</div>
              <div className="text-sm font-bold text-slate-900 mt-0.5 tabular-nums" data-testid="buyers-top-score">
                {summary.top_score.toFixed(1)}
              </div>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Persisted</div>
              <div className="text-sm font-bold text-slate-900 mt-0.5 tabular-nums" data-testid="buyers-count">
                {summary.count}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Matched buyers list */}
      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <Loader2 size={20} className="animate-spin text-slate-400 mx-auto"/>
        </div>
      ) : matches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center"
              data-testid="buyers-empty">
          <AlertTriangle size={18} className="text-amber-500 mx-auto mb-2"/>
          <div className="text-sm text-slate-700 font-semibold">No matches yet</div>
          <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            {meta ? `Considered ${meta.considered} buyers, none scored ≥ ${meta.min_score || 40}.`
                   : "Click Run Match to score buyers against this property."}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {matches.map((m, i) => (
            <div key={m.id || m.buyer_id}
                  className="rounded-xl border border-slate-200 bg-white p-3"
                  data-testid={`buyer-match-${m.buyer_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {i === 0 && <Trophy size={14} className="text-amber-500 shrink-0"/>}
                    <div className="font-semibold text-sm text-slate-900 truncate">
                      {m.buyer_name}
                    </div>
                    <CompositeBadge score={m.composite_score}/>
                    {m.status === "engaged" && (
                      <span className="text-[9px] uppercase font-bold tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                        engaged
                      </span>
                    )}
                  </div>
                </div>
                <button type="button" onClick={() => setOfferBuyer(m)}
                        className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1"
                        data-testid={`buyer-submit-offer-${m.buyer_id}`}>
                  <HandCoins size={11}/> Submit Offer
                </button>
              </div>
              <SubScorePills subScores={m.sub_scores}/>
            </div>
          ))}
        </div>
      )}

      {/* Offers panel */}
      {offers.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="offers-panel">
          <div className="flex items-center gap-2 mb-3">
            <HandCoins size={14} className="text-emerald-600"/>
            <div className="font-display font-extrabold text-base">
              Offers on this Property
            </div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
              {offers.length}
            </span>
          </div>
          <div className="space-y-2">
            {offers.map((o) => (
              <OfferCard key={o.id} offer={o}
                          onAction={async (updated) => {
                            setOffers((prev) => prev.map((x) =>
                              x.id === updated.id ? updated : x));
                            await load();
                          }}/>
            ))}
          </div>
        </div>
      )}

      {/* Workspace KPI banner (30d) */}
      {dashboard && (
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="disposition-dashboard">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={14} className="text-indigo-600"/>
            <div className="font-display font-extrabold text-base">
              Disposition KPIs · 30d
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {[
              ["Matches",   dashboard.funnel.matched,   dashboard.deltas.matched],
              ["Contacted", dashboard.funnel.contacted, dashboard.deltas.contacted],
              ["Offers",    dashboard.funnel.offered,   dashboard.deltas.offered],
              ["Closed",    dashboard.funnel.closed,    dashboard.deltas.closed],
            ].map(([label, val, delta]) => (
              <div key={label} className="rounded-md bg-slate-50 border border-slate-200 p-2"
                    data-testid={`kpi-${label.toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
                <div className="text-lg font-bold tabular-nums text-slate-900">{val}</div>
                {delta !== null && delta !== undefined && isFinite(delta) && (
                  <div className={`text-[10px] font-semibold ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {delta >= 0 ? "+" : ""}{delta}% vs prior
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Closed GMV</div>
              <div className="text-sm font-bold tabular-nums text-slate-900"
                    data-testid="kpi-closed-gmv">
                {fmtMoney(dashboard.revenue.closed_gmv)}
              </div>
            </div>
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Fees</div>
              <div className="text-sm font-bold tabular-nums text-slate-900"
                    data-testid="kpi-fees">
                {fmtMoney(dashboard.revenue.assignment_fee_realized)}
              </div>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">End-to-end</div>
              <div className="text-sm font-bold tabular-nums text-slate-900">
                {dashboard.ratios.end_to_end}%
              </div>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Active</div>
              <div className="text-sm font-bold tabular-nums text-slate-900">
                {dashboard.totals.active_buyers}b · {dashboard.totals.active_offers}o
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Coach panel */}
      <div className="rounded-xl border border-slate-200 bg-white p-4"
            data-testid="disposition-coach">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Brain size={14} className="text-violet-600"/>
            <div className="font-display font-extrabold text-base">
              AI Disposition Coach
            </div>
            {coach && (
              <span className="rounded-full px-2 py-0.5 text-[9px] font-bold bg-indigo-100 text-indigo-700">
                {Math.round((coach.confidence || 0) * 100)}% confidence
              </span>
            )}
          </div>
          <div className="flex gap-1.5">
            <select value={coachMode} onChange={(e) => setCoachMode(e.target.value)}
                     className="input text-xs" data-testid="coach-mode">
              <option value="pricing_guidance">Pricing Guidance</option>
              <option value="closing_probability">Closing Probability</option>
              <option value="buyer_risk_analysis">Buyer Risk Analysis</option>
            </select>
            <button onClick={() => generateCoach(false)} disabled={coachBusy}
                    className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"
                    data-testid="coach-generate">
              {coachBusy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              {coach ? "Refresh" : "Generate"}
            </button>
            {coach && (
              <button onClick={() => generateCoach(true)} disabled={coachBusy}
                      className="border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2 py-1.5 rounded"
                      data-testid="coach-force"
                      title="Bypass cache">
                <RotateCcw size={12}/>
              </button>
            )}
          </div>
        </div>
        {!coach ? (
          <div className="text-xs text-slate-500">
            Claude Sonnet 4.6 interprets your buyer matches + offers for this
            property. Cached by input hash to control cost.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-slate-900"
                  data-testid="coach-headline">{coach.headline}</div>
            {[
              ["strengths",              "Strengths",       "emerald"],
              ["coaching_opportunities", "Opportunities",   "amber"],
              ["recommended_actions",    "Recommended Actions", "blue"],
            ].map(([key, label, tone]) => (
              (coach[key] || []).length > 0 && (
                <div key={key}>
                  <div className={`text-[10px] uppercase tracking-wider text-${tone}-700 font-semibold mb-1`}>
                    {label}
                  </div>
                  <ul className="space-y-1 text-xs text-slate-700"
                       data-testid={`coach-${key}`}>
                    {(coach[key] || []).map((item, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className={`text-${tone}-500`}>•</span>
                        <span>
                          {item.text}
                          {item.evidence_paths?.length > 0 && (
                            <span className="ml-1 text-[10px] text-slate-400">
                              [{item.evidence_paths.join(", ")}]
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}
            {coach.data_gaps?.length > 0 && (
              <div className="text-[10px] text-amber-700 border border-amber-200 bg-amber-50 rounded p-2">
                Data gaps: {coach.data_gaps.join(", ")}
              </div>
            )}
            <div className="text-[10px] text-slate-500 italic pt-1 border-t border-slate-100">
              {coach.disclaimer}
            </div>
          </div>
        )}
      </div>

      {offerBuyer && (
        <OfferSubmitModal buyer={offerBuyer} propertyId={property.id}
                            onClose={() => setOfferBuyer(null)}
                            onSubmitted={async () => {
                              setOfferBuyer(null);
                              await load();
                            }}/>
      )}

      {campaignOpen && (
        <CampaignModal property={property}
                        onClose={() => setCampaignOpen(false)}/>
      )}

      <BuyerRolodexModal open={rolodexOpen}
                          onClose={() => setRolodexOpen(false)}
                          onChanged={load}/>
    </div>
  );
}
